import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { nicknameOnly } from '../../utils/clientIdentity';
import { ACCOUNTS } from '../../api/accounts';
import { downloadCsv } from '../../utils/downloadCsv';

const daysBetween = (dateStr, asOf) => Math.floor((new Date(asOf) - new Date(dateStr)) / 86400000);
const ageBucket = (days) => days <= 30 ? '0-30' : days <= 60 ? '31-60' : days <= 90 ? '61-90' : '90+';
const bucketColor = { '0-30': '#16a34a', '31-60': '#d97706', '61-90': '#ea580c', '90+': '#dc2626' };
const bucketBg   = { '0-30': '#f0fdf4', '31-60': '#fffbeb', '61-90': '#fff7ed', '90+': '#fef2f2' };

export default function AccountsReceivable() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('balance');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedClient, setSelectedClient] = useState(null);
  const [detail, setDetail] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState('');
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    supabase.from('branches').select('id, name, code').eq('is_active', true).order('name').then(({ data }) => setBranches(data || []));
  }, []);

  useEffect(() => {
    const fetchAll = async (table, query) => {
      const PAGE = 1000;
      let all = [], from = 0, done = false;
      while (!done) {
        const { data, error } = await query(supabase.from(table)).range(from, from + PAGE - 1);
        if (error || !data?.length) break;
        all = all.concat(data);
        if (data.length < PAGE) done = true;
        else from += PAGE;
      }
      return all;
    };

    // Read receivables straight from the general ledger: every AR line is tagged
    // with party_type='client'. Invoiced = Σ base_debit (sales), Received =
    // Σ base_credit (receipts), all in LKR base currency.
    const fetchClientLines = async () => {
      const PAGE = 1000;
      let all = [], from = 0, done = false;
      while (!done) {
        let q = supabase
          .from('journal_lines')
          .select('base_debit, base_credit, party_id, journal_entries!inner(id, entry_date, status, is_reversed, reversal_of)')
          .eq('account_id', ACCOUNTS.AR)
          .eq('party_type', 'client')
          .eq('journal_entries.status', 'POSTED')
          .lte('journal_entries.entry_date', asOf)
          // Without an explicit order, Postgres doesn't guarantee a stable row order between
          // paginated .range() calls — past 1000 rows (this account now has 1,209), some rows
          // could fall through the gap between page 1 and page 2 and silently disappear from
          // the total, which is exactly what was understating AR here.
          .order('id')
          .range(from, from + PAGE - 1);
        if (branchFilter) q = q.eq('branch_id', branchFilter);
        const { data, error } = await q;
        if (error || !data?.length) break;
        all = all.concat(data);
        if (data.length < PAGE) done = true;
        else from += PAGE;
      }
      // A reversed entry and its reversal net to zero — exclude both from gross
      // Invoiced / Received, or a corrected mistake inflates these totals. Only pairs
      // where both sides fall on/before the as-at date: an entry reversed later was
      // still outstanding on that date.
      const entryIds = new Set(all.map(l => l.journal_entries?.id));
      const reversedByAsOf = new Set(all.map(l => l.journal_entries?.reversal_of).filter(id => id && entryIds.has(id)));
      return all.filter(l => {
        const je = l.journal_entries || {};
        return !reversedByAsOf.has(je.id) && !(je.reversal_of && reversedByAsOf.has(je.reversal_of));
      });
    };

    const load = async () => {
      const [clients, lines] = await Promise.all([
        fetchAll('clients', q => q.select('id, full_name, client_code, is_active, agents:care_of_agent_id(tls_code)')),
        fetchClientLines(),
      ]);

      const map = {};
      (clients || []).forEach(c => {
        map[c.id] = { client_id: c.id, full_name: c.full_name, client_code: c.client_code, is_active: c.is_active, tls_code: c.agents?.tls_code || null, invoiced: 0, received: 0, oldestUnpaidDate: null };
      });

      (lines || []).forEach(l => {
        const id = l.party_id;
        if (!map[id]) return;
        const dr = parseFloat(l.base_debit || 0);
        const cr = parseFloat(l.base_credit || 0);
        map[id].invoiced += dr;
        map[id].received += cr;
        const date = l.journal_entries?.entry_date;
        if (dr > 0 && date && (!map[id].oldestUnpaidDate || date < map[id].oldestUnpaidDate))
          map[id].oldestUnpaidDate = date;
      });

      // Bucket the NET balance by oldest unpaid date — so buckets sum to total receivable
      const result = Object.values(map).map(r => {
        const balance = r.invoiced - r.received;
        const buckets = { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
        if (balance > 0) {
          const bucket = r.oldestUnpaidDate ? ageBucket(daysBetween(r.oldestUnpaidDate, asOf)) : '90+';
          buckets[bucket] = balance;
        }
        return { ...r, balance, buckets };
      // Deactivated clients (e.g. the reversed opening-balance placeholders) stay hidden
      // once their balance nets to zero — their history remains in the journals.
      }).filter(r => (r.invoiced > 0 || r.received > 0) && (r.is_active !== false || Math.abs(r.balance) >= 0.005));
      setRows(result);
      setLoading(false);
    };
    if (!asOf) return;
    setLoading(true);
    load();
  }, [branchFilter, asOf]);

  const sorted = useMemo(() => {
    let r = rows.filter(r => !search || r.full_name?.toLowerCase().includes(search.toLowerCase()) || r.client_code?.toLowerCase().includes(search.toLowerCase()));
    r = [...r].sort((a, b) => {
      const av = typeof a[sortKey] === 'string' ? a[sortKey].toLowerCase() : (a[sortKey] || 0);
      const bv = typeof b[sortKey] === 'string' ? b[sortKey].toLowerCase() : (b[sortKey] || 0);
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
    return r;
  }, [rows, search, sortKey, sortDir]);

  const totals = useMemo(() => sorted.reduce((acc, r) => {
    acc.invoiced += r.invoiced; acc.received += r.received; acc.balance += r.balance;
    Object.keys(r.buckets).forEach(b => acc.buckets[b] += r.buckets[b]);
    return acc;
  }, { invoiced: 0, received: 0, balance: 0, buckets: { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 } }), [sorted]);

  const handleSort = (key) => { if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('desc'); } };
  const arrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const handleExport = () => {
    if (!sorted.length) return;
    const rows = [
      ['Client Code', 'Client Name', 'Invoiced', 'Received', 'Balance Receivable', '0-30 Days', '31-60 Days', '61-90 Days', '90+ Days'],
      ...sorted.map(r => [r.client_code, r.full_name, r.invoiced.toFixed(2), r.received.toFixed(2), r.balance.toFixed(2), r.buckets['0-30'].toFixed(2), r.buckets['31-60'].toFixed(2), r.buckets['61-90'].toFixed(2), r.buckets['90+'].toFixed(2)]),
      ['', 'Totals', totals.invoiced.toFixed(2), totals.received.toFixed(2), totals.balance.toFixed(2), totals.buckets['0-30'].toFixed(2), totals.buckets['31-60'].toFixed(2), totals.buckets['61-90'].toFixed(2), totals.buckets['90+'].toFixed(2)],
    ];
    downloadCsv(rows, `accounts-receivable-as-at-${asOf}.csv`);
  };

  const openClient = async (client) => {
    if (selectedClient?.client_id === client.client_id) { setSelectedClient(null); return; }
    setSelectedClient(client);
    setDetailLoading(true);
    const [{ data: t }, { data: v }, { data: bk }, { data: rv }, { data: inv }] = await Promise.all([
      supabase.from('tickets').select('id, invoice_id, ticket_ref, airline, route_from, route_to, departure_date, sell_price, sell_currency:sell_currency_id(code), payment_status, created_at').eq('client_id', client.client_id).order('created_at', { ascending: false }).limit(500),
      supabase.from('visas').select('id, invoice_id, visa_ref, visa_types(name, country), sell_price, sell_currency:sell_currency_id(code), payment_status, created_at').eq('client_id', client.client_id).order('created_at', { ascending: false }).limit(500),
      supabase.from('bookings').select('id, booking_ref, packages(package_name, package_code), total_sell, sell_currency:sell_currency_id(code), payment_status, created_at').eq('client_id', client.client_id).order('created_at', { ascending: false }).limit(500),
      supabase.from('receipt_vouchers').select('voucher_number, voucher_date, amount, currency:currency_id(code), payment_mode').eq('client_id', client.client_id).order('voucher_date', { ascending: false }).limit(500),
      // Fetch every invoice for this client (not just direct ones) — bookings link back via
      // source_type/source_id rather than their own invoice_id column, so we need the full
      // set to build that lookup below.
      supabase.from('invoices').select('id, invoice_number, source_type, source_id, line_items, status, created_at').eq('client_id', client.client_id).neq('status', 'VOID').order('created_at', { ascending: false }).limit(500),
    ]);
    // Build source IDs from linked records to avoid duplicating direct invoices
    const linkedSourceIds = new Set([...(t || []).map(x => x.id), ...(v || []).map(x => x.id), ...(bk || []).map(x => x.id)]);
    const directInvoices = (inv || []).filter(x => !x.source_id || !linkedSourceIds.has(x.source_id));
    const bookingInvoiceMap = new Map((inv || []).filter(x => x.source_type === 'booking' && x.source_id).map(x => [x.source_id, x.id]));
    const invoices = [
      ...(t  || []).map(x => ({ ...x, type: 'Ticket',  ref: x.ticket_ref,   desc: `${x.airline} ${x.route_from}→${x.route_to}`, date: x.departure_date || x.created_at, sell_price: x.sell_price })),
      ...(v  || []).map(x => ({ ...x, type: 'Visa',    ref: x.visa_ref,     desc: `${x.visa_types?.country} — ${x.visa_types?.name}`, date: x.created_at })),
      ...(bk || []).map(x => ({ ...x, type: 'Package', ref: x.booking_ref,  desc: x.packages?.package_name || 'Package', date: x.created_at, sell_price: x.total_sell, invoice_id: bookingInvoiceMap.get(x.id) || null })),
      ...(directInvoices).map(x => {
        const li = x.line_items?.[0];
        return { ...x, type: x.source_type || 'Invoice', ref: x.invoice_number, desc: li?.description || x.invoice_number, date: x.created_at, sell_price: li?.amount || 0, payment_status: x.status, invoice_id: x.id };
      }),
    ].sort((a, b) => new Date(b.date) - new Date(a.date));
    const payments = (rv || []).map(x => ({ ...x, type: 'Receipt', ref: x.voucher_number, date: x.voucher_date }));
    setDetail({ invoices, payments });
    setDetailLoading(false);
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Accounts Receivable</div>
          <div className="page-subtitle">Outstanding client balances with aging — as at {asOf}</div>
        </div>
        <div className="no-print" style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={handleExport}>Download CSV</button>
          <button className="btn btn-secondary" onClick={() => window.print()}>Print</button>
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 16 }}>
        <div className="stat-card" style={{ borderLeft: '3px solid #dc2626' }}>
          <div className="stat-label">Total Receivable</div>
          <div className="stat-value" style={{ color: '#dc2626' }}>LKR {totals.balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '3px solid #7c3aed' }}>
          <div className="stat-label">Clients with Balance</div>
          <div className="stat-value" style={{ color: '#7c3aed' }}>{sorted.filter(r => r.balance > 0).length}</div>
        </div>
      </div>

      {/* Aging buckets */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
        {['0-30', '31-60', '61-90', '90+'].map(b => (
          <div key={b} style={{ background: bucketBg[b], border: `1px solid ${bucketColor[b]}30`, borderRadius: 8, padding: '12px 16px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: bucketColor[b], marginBottom: 4 }}>{b} DAYS</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: bucketColor[b] }}>LKR {totals.buckets[b].toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          </div>
        ))}
      </div>

      {/* Search + Branch filter */}
      <div className="card no-print" style={{ padding: '12px 16px', marginBottom: 0, borderRadius: '10px 10px 0 0', display: 'flex', gap: 12, alignItems: 'center' }}>
        <input className="form-input" style={{ maxWidth: 320 }} placeholder="Search client name or code…" value={search} onChange={e => setSearch(e.target.value)} />
        <select className="form-input" style={{ maxWidth: 200 }} value={branchFilter} onChange={e => setBranchFilter(e.target.value)}>
          <option value="">All Branches</option>
          {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <label className="form-label" style={{ margin: 0, whiteSpace: 'nowrap' }}>As at</label>
        <input className="form-input" style={{ maxWidth: 170 }} type="date" value={asOf} onChange={e => setAsOf(e.target.value)} />
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, borderRadius: '0 0 10px 10px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
              {[['full_name', 'Client'], ['invoiced', 'Invoiced'], ['received', 'Received'], ['balance', 'Balance Due']].map(([key, label]) => (
                <th key={key} onClick={() => handleSort(key)} style={{ padding: '10px 14px', textAlign: key === 'full_name' ? 'left' : 'right', cursor: 'pointer', userSelect: 'none', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                  {label}{arrow(key)}
                </th>
              ))}
              {['0-30', '31-60', '61-90', '90+'].map(b => (
                <th key={b} style={{ padding: '10px 10px', textAlign: 'right', fontSize: 11, color: bucketColor[b], fontWeight: 700 }}>{b}d</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(row => (
              <React.Fragment key={row.client_id}>
                <tr
                  onClick={() => openClient(row)}
                  style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', background: selectedClient?.client_id === row.client_id ? 'rgba(79,110,247,0.04)' : '' }}
                  onMouseEnter={e => { if (selectedClient?.client_id !== row.client_id) e.currentTarget.style.background = 'var(--bg)'; }}
                  onMouseLeave={e => { if (selectedClient?.client_id !== row.client_id) e.currentTarget.style.background = ''; }}
                >
                  <td style={{ padding: '10px 14px', display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    {row.tls_code && <span style={{ color: '#f07040', fontWeight: 600 }}>{row.tls_code}</span>}
                    <span style={{ fontWeight: 600 }}>{nicknameOnly(row.client_code) || row.client_code}</span>
                    <span
                      onClick={e => { e.stopPropagation(); navigate(`/ledger/clients?client=${row.client_id}`); }}
                      style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--primary)', textDecoration: 'underline', cursor: 'pointer' }}
                      title="Open this client's ledger"
                    >
                      Ledger →
                    </span>
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}>{row.invoiced.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', color: '#16a34a' }}>{row.received.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: row.balance > 0 ? '#dc2626' : '#16a34a' }}>{row.balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  {['0-30', '31-60', '61-90', '90+'].map(b => (
                    <td key={b} style={{ padding: '10px 10px', textAlign: 'right', color: row.buckets[b] > 0 ? bucketColor[b] : 'var(--text-muted)', fontWeight: row.buckets[b] > 0 ? 600 : 400, fontSize: 12 }}>
                      {row.buckets[b] > 0 ? row.buckets[b].toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}
                    </td>
                  ))}
                </tr>

                {/* Drill-down */}
                {selectedClient?.client_id === row.client_id && (
                  <tr><td colSpan={8} style={{ padding: 0, background: '#f8fafc' }}>
                    {detailLoading ? (
                      <div style={{ padding: 20, textAlign: 'center' }}><div className="spinner" /></div>
                    ) : (
                      <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>Invoices</div>
                          {detail.invoices?.length === 0 ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>None</div> : detail.invoices?.map(inv => (
                            <div key={inv.id} onClick={() => inv.invoice_id ? navigate(`/invoices/${inv.invoice_id}`) : navigate(`/${inv.type === 'Ticket' ? 'tickets' : inv.type === 'Package' ? 'bookings' : 'visas'}/${inv.id}`)} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: 12, cursor: 'pointer' }}>
                              <div><span style={{ fontWeight: 600, color: 'var(--primary)' }}>{inv.ref}</span><span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>{inv.desc}</span></div>
                              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                <span>{inv.sell_currency?.code} {parseFloat(inv.sell_price || 0).toLocaleString()}</span>
                                <span style={{ fontSize: 10, fontWeight: 600, color: inv.payment_status === 'PAID' ? '#16a34a' : '#dc2626' }}>{inv.payment_status}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>Receipts</div>
                          {detail.payments?.length === 0 ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>None</div> : detail.payments?.map((p, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                              <div><span style={{ fontWeight: 600 }}>{p.ref}</span><span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>{p.date?.slice(0, 10)} · {p.payment_mode}</span></div>
                              <span style={{ color: '#16a34a', fontWeight: 600 }}>{p.currency?.code} {parseFloat(p.amount || 0).toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: 'var(--bg)', borderTop: '2px solid var(--border)', fontWeight: 700 }}>
              <td style={{ padding: '10px 14px' }}>TOTAL ({sorted.length} clients)</td>
              <td style={{ padding: '10px 14px', textAlign: 'right' }}>{totals.invoiced.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
              <td style={{ padding: '10px 14px', textAlign: 'right', color: '#16a34a' }}>{totals.received.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
              <td style={{ padding: '10px 14px', textAlign: 'right', color: '#dc2626' }}>{totals.balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
              {['0-30', '31-60', '61-90', '90+'].map(b => (
                <td key={b} style={{ padding: '10px 10px', textAlign: 'right', color: bucketColor[b], fontSize: 12 }}>{totals.buckets[b].toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
